/**
 * Shared types for Atlas Escalation Policies (ADR-0071).
 *
 * Mirrors hub shapes in atlas-hub/services/hub/src/escalation/repos/escalationPolicies.ts.
 * API/DB values use `group` for team-based target mode; all UI labels say "Team".
 */

export type TierKind = "acknowledgment" | "resolution";
export type TierTargetMode = "chain_hop" | "explicit" | "group";
export type TierAction = "notify" | "reassign";
export type OnChainExhausted = "stop" | "continue";

export const TIER_KINDS: TierKind[] = ["acknowledgment", "resolution"];

export const TIER_KIND_LABELS: Record<TierKind, string> = {
  acknowledgment: "Acknowledgment",
  resolution: "Resolution"
};

export const TIER_TARGET_MODE_LABELS: Record<TierTargetMode, string> = {
  chain_hop: "Chain hop",
  explicit: "Specific users",
  group: "Team"
};

export const TIER_ACTION_LABELS: Record<TierAction, string> = {
  notify: "Notify only",
  reassign: "Reassign & notify"
};

export interface EscalationPolicy {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EscalationPolicySummary extends EscalationPolicy {
  tiers_count: number;
  tiers_summary: string;
}

export interface EscalationTier {
  id: string;
  policy_id: string;
  kind: TierKind;
  timer_minutes: number;
  target_mode: TierTargetMode;
  explicit_account_ids: string[];
  group_id: string | null;
  action: TierAction;
  on_chain_exhausted: OnChainExhausted;
  label: string | null;
  created_at: string;
}

export interface EscalationPolicyWithTiers extends EscalationPolicy {
  tiers: EscalationTier[];
}

export interface TierInput {
  kind: TierKind;
  timer_minutes: number;
  target_mode: TierTargetMode;
  explicit_account_ids: string[];
  group_id: string | null;
  action: TierAction;
  on_chain_exhausted: OnChainExhausted;
  label: string | null;
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (rem === 0) return `${hours}h`;
  return `${hours}h ${rem}m`;
}

/**
 * ADR-0154 §6.3 C10 — `formatTiersSummary` was deleted here.
 *
 * The hub computes and serves `tiers_summary`, and the console already renders
 * the served value. This function had no non-test caller: a second, silently
 * diverging implementation of a string the server already produces. Dead
 * duplication is still duplication — it is what someone reaches for next.
 */
